import { useState } from 'react'

import { Button } from '../../ui/Control'

export interface IssuedProps {
  readonly name: string
  readonly token: string
  readonly endpoint: string
  readonly onDone: () => void
}

/**
 * The one moment a token exists outside the program that will hold it.
 *
 * Everything about this panel follows from that. It says so **before** the value, not underneath it,
 * because somebody who reads the warning after closing the panel has been told nothing. It offers
 * copying rather than expecting anybody to select ninety characters by hand on a phone. And it will
 * not go away by itself: there is one control, it is called *Done*, and pressing it is somebody
 * saying they have the value somewhere.
 *
 * The address is beside the token because the two are always used together — a client asks for both
 * in the same dialog, and sending somebody back to the top of the page for the second one is the
 * kind of small friction that ends with a token pasted into the wrong field.
 */
export function Issued({ name, token, endpoint, onDone }: IssuedProps) {
  const [copied, setCopied] = useState<'token' | 'endpoint' | null>(null)

  const copy = async (what: 'token' | 'endpoint', value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(what)
    } catch {
      /* Some browsers refuse without a gesture they recognise. The value is on screen either way. */
      setCopied(null)
    }
  }

  return (
    <div className="issued" role="group" aria-label={`The token for ${name}`}>
      <h3 className="issued__title">{name} is ready</h3>
      <p className="issued__warning">
        This is the only time MyLib shows this token. Put it into the assistant now — if it is lost,
        revoke this access and make another.
      </p>

      <label className="field__label" htmlFor="issued-endpoint">
        Address
      </label>
      <div className="issued__value">
        <input id="issued-endpoint" className="issued__text" readOnly value={endpoint} />
        <Button
          onClick={() => {
            void copy('endpoint', endpoint)
          }}
        >
          {copied === 'endpoint' ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <label className="field__label" htmlFor="issued-token">
        Token
      </label>
      <div className="issued__value">
        <input
          id="issued-token"
          className="issued__text issued__text--token"
          readOnly
          value={token}
        />
        <Button
          tone="primary"
          onClick={() => {
            void copy('token', token)
          }}
        >
          {copied === 'token' ? 'Copied' : 'Copy'}
        </Button>
      </div>

      <div className="controls">
        <Button onClick={onDone}>Done</Button>
      </div>
    </div>
  )
}
